/**
 * InAppCheckout — embedded Stripe PaymentElement checkout.
 *
 * in-app-checkout Phase 3 Session 3.3.
 *
 * Flow:
 *   1. Caller passes `tierId` + optional `conversation`. Component fetches
 *      `POST /api/billing/subscription` via `usePaymentIntent()` to obtain the
 *      `client_secret`.
 *   2. On success, wraps the Stripe `<Elements>` provider + `<PaymentElement>`
 *      around an order-review panel. User clicks "Subscribe" → `confirmPayment`
 *      runs (3DS/SCA handled inline via `redirect: 'if_required'`).
 *   3. On payment confirmation:
 *      - `status === 'succeeded'` → calls `onSuccess()` (caller handles nav).
 *      - Error → surfaces `BILLING_ERROR_COPY` copy inline.
 *
 * Design fidelity:
 *   - Membership aesthetic: font-serif header, MembershipEyebrow eyebrow,
 *     /-bullet feature box, `bg-accent text-void` CTA (mirrors CheckoutTierCard
 *     and the reference `dashboard-arcanum/src/pages/PricingPage.tsx`).
 *   - Token discipline: no raw hex — whitelisted inline rgba() for accent-glow
 *     shadow only (same exemption as CheckoutTierCard recommended glow).
 *   - Billing-copy HARD RULE: no `$` on usage surfaces, no "Anthropic", no
 *     COGS math. Price display IS allowed on checkout (the user needs to see
 *     what they are paying); `billing_reason` invoice labels are usage-adjacent
 *     so are kept to Stripe's safe description strings.
 *
 * Stripe appearance mapping (Intelligence Terminal dark theme):
 *   - theme: 'night'
 *   - background.color: var(--color-panel-bg) → #070a0f
 *   - primary: var(--color-accent) → #00c853 (input labels, focus rings)
 *   - text.default: var(--color-text-bright) → #e4e8ee
 *
 * 4-state coverage (ALL required):
 *   - LOADING   — fetching client_secret; shows Skeleton
 *   - ERROR     — failed to create subscription; shows retry prompt
 *   - READY     — PaymentElement rendered, user fills card details
 *   - SUCCESS   — confirmPayment resolved; calls onSuccess
 *
 * PostHog events:
 *   - `checkout_started` — fires when the component mounts with a valid
 *     `tierId` (StrictMode-guarded, ref-dedupe). Mirrors the existing
 *     CheckoutTierCard event so the funnel stays coherent.
 *   - `checkout_completed` — fires on `confirmPayment` success before `onSuccess`
 *     callback (mirrors `<CheckoutSuccessPolling>` PostHog event name).
 *
 * Sentry:
 *   - surface tag `checkout-confirm` (replaces the retired `stripe-redirect`
 *     site). Tags: `tier_id`, `code` (ApiError only), `stripe_error_type`
 *     (Stripe.js `StripeError.type`).
 *
 * @param tierId      — the tier being purchased (e.g. `'principal'`)
 * @param tierName    — human display name (e.g. `'Principal'`), rendered in header
 * @param tierPrice   — display price string (e.g. `'$499 / mo'`) — caller formats
 * @param conversation — optional deep-link conversation id for post-subscribe nav
 * @param onSuccess   — called after `confirmPayment` resolves with status 'succeeded'
 * @param onBack      — called when user clicks the ← back affordance
 */

import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { ArrowLeft, RefreshCw, ShieldCheck } from 'lucide-react';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import type { StripeElementsOptions, Stripe } from '@stripe/stripe-js';
import * as Sentry from '@sentry/react';
import { Button } from '@/components/ui/Button';
import { Panel } from '@/components/ui/Panel';
import { Skeleton } from '@/components/ui/Skeleton';
import { posthog } from '@/lib/posthog';
import { ApiError } from '@/lib/api';
import { usePaymentIntent } from '@/hooks/use-payment-intent';
import { useBillingUsage, asBillingUsageRow } from '@/hooks/use-billing-usage';
import { STRIPE_APPEARANCE } from '@/lib/stripe';
import { BILLING_ERROR_COPY } from './billing-error-copy';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface InAppCheckoutProps {
  /** The Stripe-connected tier id (e.g. `'principal'`). */
  tierId: string;
  /** Human-readable tier name shown in the order header. */
  tierName: string;
  /**
   * Formatted price string shown to the user, e.g. `"$499 / mo"`.
   * Caller owns formatting (whole-dollar, no cents if not needed).
   */
  tierPrice: string;
  /**
   * Optional conversation id to thread into the post-subscribe nav so
   * the user lands back on their deal after subscribing.
   */
  conversation?: string;
  /** Called after `confirmPayment` resolves successfully. */
  onSuccess: () => void;
  /** Called when the user clicks the ← back affordance. */
  onBack?: () => void;
  /**
   * Stripe singleton. Passed as a prop so the caller controls when
   * `loadStripe` is invoked (singleton at the app/route level) and the
   * test harness can inject a mock without global state.
   */
  stripe: Stripe | null;
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

/**
 * InAppCheckout — outer shell. Calls `usePaymentIntent` to obtain the
 * `client_secret`, then renders the `<Elements>` provider + inner form.
 *
 * Renders a Skeleton on load, an error state on failure, and the checkout
 * form once the `client_secret` arrives.
 */
export function InAppCheckout({
  tierId,
  tierName,
  tierPrice,
  conversation,
  onSuccess,
  onBack,
  stripe,
}: InAppCheckoutProps) {
  // NOTE: we deliberately do NOT consume the mutation's `isPending` for the
  // loading gate. Under the D6 single-call dedup, the live React-Query observer
  // doesn't own the `mutateAsync` call, so its `isPending` can wedge `true` in
  // StrictMode — gating loading on `!clientSecret` instead is both sufficient
  // and immune to that. `error` is still surfaced (it carries the typed code).
  const { mutateAsync: createSubscription, error } = usePaymentIntent();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // D5 (e2e-walk 2026-06-06): after a successful confirmPayment / immediate-
  // succeeded subscription, the `usage_balance` unlock is driven by the async
  // `invoice.paid` / `customer.subscription.updated` webhook (1-3s tail via
  // `stripe listen` locally; similar on Cloud Run). Calling onSuccess() →
  // navigate('/cockpit') immediately RACES that unlock: the root cascade reads
  // `locked=true` and bounces the user straight back to /billing/checkout —
  // i.e. "I paid and landed back on the pricing grid." Mirror the retired
  // <CheckoutSuccessPolling> contract: enter a `finalizing` state that polls
  // usage every 1s until `locked===false`, THEN call onSuccess. A 15s timeout
  // falls through to onSuccess so a delayed/dropped webhook never strands them.
  const [finalizing, setFinalizing] = useState(false);
  const { data: balance } = useBillingUsage(
    finalizing ? { refetchInterval: 1000 } : undefined
  );

  useEffect(() => {
    if (!finalizing) return;
    const row = asBillingUsageRow(balance ?? null);
    if (row && row.locked === false) {
      onSuccess();
    }
  }, [finalizing, balance, onSuccess]);

  useEffect(() => {
    if (!finalizing) return;
    const t = setTimeout(() => onSuccess(), 15000);
    return () => clearTimeout(t);
  }, [finalizing, onSuccess]);

  // StrictMode ref-dedupe: fires checkout_started exactly once per real mount.
  const startedRef = useRef(false);

  // D6 (e2e-walk 2026-06-06): memoize the subscription-create call so it fires
  // EXACTLY ONCE per (tierId, conversation). React 18 StrictMode double-invokes
  // effects in dev, which previously fired TWO `POST /api/billing/subscription`
  // calls → TWO Stripe subscriptions. The usage_balance row tracked one while
  // the PaymentElement charged the other, so the `invoice.paid` unlock
  // (`UPDATE … WHERE stripe_subscription_id = <paid sub>`) matched no row and
  // the paying subscriber stayed `locked=true` (bounced back to the pricing
  // grid). Sharing ONE promise across both effect runs creates one subscription.
  const fetchedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    posthog.capture('checkout_started', { tier_id: tierId });
  }, [tierId]);

  // Fetch the payment intent on mount (eagerly — user already chose tier).
  // Handles two arms of the discriminated union from the API:
  //   succeeded      → payment already completed server-side (card on file, no 3DS);
  //                    fire checkout_completed + onSuccess immediately, skip PaymentElement.
  //   requires_action → 3DS/SCA needed; store client_secret and render PaymentElement.
  useEffect(() => {
    // D6: fire createSubscription EXACTLY ONCE per (tierId, conversation). The
    // key-guard ref survives React 18 StrictMode's mount→unmount→mount in dev,
    // so the double-invoked effect issues a single `POST /api/billing/subscription`
    // (one Stripe subscription). A genuine dep change re-fetches (key mismatch).
    //
    // The result is applied UNCONDITIONALLY — we deliberately do NOT gate it on
    // an `active`/cleanup flag. StrictMode runs the effect's cleanup between the
    // two invocations, so an `active`-gated handler would see `active===false`
    // on BOTH runs and never call setClientSecret (the bug this replaced). A
    // setState after a *real* unmount is a harmless no-op in React 18.
    const key = `${tierId}|${conversation ?? ''}`;
    if (fetchedKeyRef.current === key) return;
    fetchedKeyRef.current = key;
    setFetchError(null);

    createSubscription({ tier_id: tierId, conversation })
      .then((result) => {
        if (result.status === 'succeeded') {
          // Payment already confirmed server-side (e.g. $0 invoice / coupon).
          // Enter `finalizing` (poll for the webhook unlock) instead of
          // navigating immediately — see the D5 note above.
          posthog.capture('checkout_completed', { tier_id: tierId });
          setFinalizing(true);
        } else {
          // requires_action — render PaymentElement for the user to confirm.
          setClientSecret(result.client_secret);
        }
      })
      .catch((err: unknown) => {
        // Re-allow a retry of this key (e.g. the `Try again` reload path).
        fetchedKeyRef.current = null;
        // All errors from subscription-create surface as the same user-facing
        // message — the distinction (ApiError vs network error) is Sentry-only.
        setFetchError(BILLING_ERROR_COPY.SUBSCRIPTION_CREATE_FAILED);
        Sentry.captureException(err, {
          tags: {
            surface: 'checkout-confirm',
            tier_id: tierId,
            ...(err instanceof ApiError ? { code: err.code } : {}),
          },
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tierId, conversation]);

  // --- Finalizing (payment confirmed; waiting on the webhook unlock) ---
  // Short-circuits BEFORE loading/ready so neither the skeleton nor the form
  // flashes back while we poll usage for `locked===false` (D5 fix above).
  if (finalizing) {
    return (
      <div
        data-testid="in-app-checkout-finalizing"
        className="flex flex-col items-center justify-center gap-4 py-16"
        role="status"
        aria-live="polite"
      >
        <div className="flex items-center gap-3">
          <svg
            width="20"
            height="24"
            viewBox="0 0 16 18"
            className="animate-pulse"
          >
            <rect
              x="0"
              y="0"
              width="5"
              height="18"
              rx="3"
              fill="var(--color-accent)"
            />
            <rect
              x="8"
              y="0"
              width="5"
              height="18"
              rx="3"
              fill="var(--color-accent)"
            />
          </svg>
          <span className="font-mono text-sm text-text-bright">
            Activating your membership…
          </span>
        </div>
        <p className="font-body text-xs text-text-muted">
          Payment confirmed. Finalizing your account — this takes a moment.
        </p>
      </div>
    );
  }

  // --- Loading (until the client_secret arrives or the create call errors) ---
  if (!clientSecret && !fetchError) {
    return (
      <div
        data-testid="in-app-checkout-loading"
        className="flex flex-col gap-4"
      >
        <CheckoutHeader
          tierName={tierName}
          tierPrice={tierPrice}
          onBack={onBack}
        />
        <Skeleton className="h-48" />
        <Skeleton className="h-12" />
      </div>
    );
  }

  // --- Error (subscription create failed) ---
  if (fetchError || error) {
    const message = fetchError ?? BILLING_ERROR_COPY.SUBSCRIPTION_CREATE_FAILED;
    return (
      <div data-testid="in-app-checkout-error" className="flex flex-col gap-4">
        <CheckoutHeader
          tierName={tierName}
          tierPrice={tierPrice}
          onBack={onBack}
        />
        <div className="rounded-[3px] border border-danger/15 bg-danger-dim px-4 py-3">
          <p
            data-testid="in-app-checkout-error-message"
            className="font-mono text-xs text-danger"
          >
            {message}
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => {
            setFetchError(null);
            startedRef.current = false;
            // Trigger re-fetch by clearing clientSecret — effect deps unchanged
            // so we manually reset and re-fire via key trick on the parent.
            // Simplest approach: reload page as last resort.
            window.location.reload();
          }}
          data-testid="in-app-checkout-retry"
        >
          Try again
        </Button>
      </div>
    );
  }

  // --- Ready (client_secret obtained, render Stripe Elements) ---
  const options: StripeElementsOptions = {
    clientSecret: clientSecret!,
    appearance: STRIPE_APPEARANCE,
  };

  return (
    <div data-testid="in-app-checkout-ready">
      <CheckoutHeader
        tierName={tierName}
        tierPrice={tierPrice}
        onBack={onBack}
      />
      <Elements stripe={stripe} options={options}>
        <CheckoutForm
          tierId={tierId}
          tierName={tierName}
          tierPrice={tierPrice}
          // On confirmPayment success, enter `finalizing` (poll for the webhook
          // unlock) rather than navigating immediately — see the D5 note above.
          onSuccess={() => setFinalizing(true)}
        />
      </Elements>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Order-details helpers (prototype §B spec — FIX 2)
// ---------------------------------------------------------------------------

/** Single label/value line in the Order details panel. */
interface OrderRowProps {
  label: string;
  sublabel?: string;
  value: string;
  /** `'total'` = bold label + accent value; default = muted label + bright value. */
  emphasis?: 'total';
  /** Adds a top border before this row. */
  divider?: boolean;
}

function OrderRow({
  label,
  sublabel,
  value,
  emphasis,
  divider,
}: OrderRowProps) {
  return (
    <div
      className={clsx(
        'flex items-start justify-between gap-4 py-2.5',
        divider && 'border-t border-panel-border'
      )}
    >
      <span className="flex flex-col gap-0.5">
        <span
          className={clsx(
            'font-mono uppercase tracking-[0.5px]',
            emphasis === 'total'
              ? 'text-[10px] font-semibold text-text-bright'
              : 'text-[9px] text-text-muted'
          )}
        >
          {label}
        </span>
        {sublabel && (
          <span className="font-mono text-[9px] tracking-[0.4px] text-text-dim">
            {sublabel}
          </span>
        )}
      </span>
      <span
        className={clsx(
          'font-mono tabular-nums',
          emphasis === 'total'
            ? 'text-md font-bold text-accent'
            : 'text-sm text-text-bright'
        )}
      >
        {value}
      </span>
    </div>
  );
}

/** Auto-renew / notice bar (prototype § NoticeBar — neutral/info tone). */
function NoticeBar({
  children,
  testid,
}: {
  children: React.ReactNode;
  testid?: string;
}) {
  return (
    <div
      data-testid={testid}
      role="status"
      className="flex items-start gap-2.5 rounded-[3px] border border-panel-border bg-input-bg px-3.5 py-3"
    >
      <span className="mt-0.5 shrink-0 text-text-muted">
        <RefreshCw size={13} />
      </span>
      <span className="font-body text-[11px] leading-relaxed text-text-muted">
        {children}
      </span>
    </div>
  );
}

/** Short US date — `Jun 5, 2026`. */
function formatRenewDate(d: Date): string {
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// Inner form (must be INSIDE <Elements> to access useStripe/useElements)
// ---------------------------------------------------------------------------

interface CheckoutFormProps {
  tierId: string;
  tierName: string;
  tierPrice: string;
  onSuccess: () => void;
}

function CheckoutForm({
  tierId,
  tierName,
  tierPrice,
  onSuccess,
}: CheckoutFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    setPaymentError(null);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // No return_url needed — redirect: 'if_required' handles 3DS inline.
      },
      redirect: 'if_required',
    });

    if (error) {
      // Map Stripe error types to BILLING_ERROR_COPY keys.
      let copy: string;
      if (
        error.type === 'card_error' &&
        error.code === 'authentication_required'
      ) {
        copy = BILLING_ERROR_COPY.AUTH_REQUIRED;
      } else if (error.type === 'validation_error') {
        // Stripe handles inline validation in the iframe — this is a rare
        // fallthrough (e.g. unsupported card brand). Show the raw Stripe message.
        copy = error.message ?? BILLING_ERROR_COPY.CONFIRM_PAYMENT_FAILED;
      } else if (error.type === 'card_error') {
        copy = BILLING_ERROR_COPY.PAYMENT_DECLINED;
      } else if (error.type === 'authentication_error') {
        copy = BILLING_ERROR_COPY.AUTH_FAILED;
      } else {
        copy = error.message ?? BILLING_ERROR_COPY.CONFIRM_PAYMENT_FAILED;
      }

      setPaymentError(copy);
      setSubmitting(false);

      Sentry.captureException(
        new Error(error.message ?? 'stripe_confirm_error'),
        {
          tags: {
            surface: 'checkout-confirm',
            tier_id: tierId,
            stripe_error_type: error.type,
            ...(error.code ? { stripe_error_code: error.code } : {}),
          },
        }
      );
      return;
    }

    // Payment confirmed successfully.
    posthog.capture('checkout_completed', { tier_id: tierId });
    setSubmitting(false);
    onSuccess();
  }

  // Auto-renew date: 30 days from now (monthly billing cycle).
  const renewDate = formatRenewDate(
    new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
  );

  return (
    <form
      onSubmit={(e) => void handleSubmit(e)}
      data-testid="checkout-payment-form"
      className="flex flex-col gap-4 mt-5"
    >
      {/* Order details — titled Panel with line-item rows (FIX 2 / prototype §B).
          NO tax row (SaaS spec §1). Composition: tier name + price → Subtotal
          divider → Total due today emphasized. */}
      <Panel title="Order details">
        <div className="flex flex-col" data-testid="checkout-order-details">
          <OrderRow
            label={`${tierName} membership`}
            sublabel="Monthly"
            value={tierPrice}
          />
          <OrderRow label="Subtotal" value={tierPrice} divider />
          <OrderRow
            label="Total due today"
            value={tierPrice}
            emphasis="total"
            divider
          />
        </div>
      </Panel>

      {/* Auto-renew notice — monthly. No "+ tax" (no sales tax collected).
          Matches prototype §B NoticeBar (RefreshCw icon + copy). */}
      <NoticeBar testid="checkout-auto-renew-notice">
        Your subscription auto-renews monthly at {tierPrice} on {renewDate}.
        Cancel anytime in your account settings.
      </NoticeBar>

      {/* Payment method — Stripe PaymentElement iframe (card data never leaves Stripe) */}
      <Panel title="Payment method">
        <div data-testid="stripe-payment-element-wrapper">
          <PaymentElement
            id="payment-element"
            options={{
              layout: 'tabs',
            }}
          />
        </div>
      </Panel>

      {/* Inline payment error */}
      {paymentError && (
        <div
          role="alert"
          aria-live="assertive"
          data-testid="checkout-payment-error"
          className="rounded-[3px] border border-danger/15 bg-danger-dim px-4 py-2.5"
        >
          <p className="font-mono text-xs text-danger">{paymentError}</p>
        </div>
      )}

      {/* Submit CTA — Membership aesthetic (mirrors CheckoutTierCard CTA) */}
      <button
        type="submit"
        disabled={submitting || !stripe || !elements}
        data-testid="checkout-submit-btn"
        className={clsx(
          'w-full py-4',
          'bg-accent border border-accent rounded-[3px]',
          'font-mono text-[0.78rem] font-medium uppercase tracking-[0.14em]',
          'text-void cursor-pointer hover:brightness-110 transition-[filter] duration-200',
          'disabled:opacity-60 disabled:cursor-not-allowed'
        )}
      >
        {submitting ? 'Processing…' : `Subscribe to ${tierName}`}
      </button>

      {/* Security assurance */}
      <div className="flex items-center justify-center gap-1.5 text-text-dim">
        <ShieldCheck size={13} />
        <span className="font-mono text-[9px] uppercase tracking-[0.8px]">
          Secured by Stripe · Card data never touches our servers
        </span>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Shared header (reused by loading / error / ready states)
// ---------------------------------------------------------------------------

interface CheckoutHeaderProps {
  tierName: string;
  tierPrice: string;
  onBack?: () => void;
}

function CheckoutHeader({ tierName, tierPrice, onBack }: CheckoutHeaderProps) {
  return (
    <div className="mb-2 flex items-start gap-3">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          aria-label="Back"
          data-testid="checkout-back-btn"
          className="mt-1.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-[2px] border border-panel-border bg-panel-bg text-text-muted transition-colors hover:border-panel-border-hi hover:text-text-bright"
        >
          <ArrowLeft size={14} />
        </button>
      )}
      <div className="flex flex-col gap-1">
        {/* MEMBERSHIP eyebrow */}
        <div className="font-mono text-[10px] font-medium uppercase tracking-[0.18em] text-accent">
          Membership
        </div>
        <h2 className="font-serif text-[26px] font-semibold leading-[1.1] tracking-[-0.01em] text-text-bright">
          {tierName}
        </h2>
        <div className="font-mono text-sm font-medium tabular-nums text-text-muted">
          {tierPrice}
        </div>
      </div>
    </div>
  );
}
